import { useState, useEffect, useContext } from 'react';
import { AuthContext } from '@/App';
import { Phone, TrendingUp, TrendingDown, Minus, Clock, PhoneMissed } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDuration = (seconds) => {
  if (!seconds) return '0m';
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  if (hrs > 0) return `${hrs}h ${mins}m`;
  return `${mins}m`;
};

const CallAnalytics = () => {
  const { token } = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/calls/analytics`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setData(res.data);
      } catch (err) {}
      setLoading(false);
    };
    fetch();
  }, [token]);

  if (loading) return null;
  if (!data) return null;

  const trendIcon = data.trend === 'up' ? <TrendingUp className="w-4 h-4 text-green-500" /> :
                    data.trend === 'down' ? <TrendingDown className="w-4 h-4 text-red-500" /> :
                    <Minus className="w-4 h-4 text-gray-400" />;

  const maxDaily = Math.max(...data.daily.map(d => d.count), 1);

  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100" data-testid="call-analytics-widget">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-gray-900">Call Activity</h3>
        <div className="flex items-center space-x-1 text-sm text-gray-500">
          {trendIcon}
          <span>{data.trend === 'up' ? 'More' : data.trend === 'down' ? 'Fewer' : 'Same'} vs last week</span>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        <div className="bg-teal/5 rounded-xl p-3 text-center">
          <div className="flex items-center justify-center space-x-1 mb-1">
            <Phone className="w-4 h-4 text-teal" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{data.this_week}</p>
          <p className="text-xs text-gray-500">This week</p>
        </div>
        <div className="bg-red-50 rounded-xl p-3 text-center">
          <div className="flex items-center justify-center space-x-1 mb-1">
            <PhoneMissed className="w-4 h-4 text-red-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{data.missed_this_week}</p>
          <p className="text-xs text-gray-500">Missed</p>
        </div>
        <div className="bg-blue-50 rounded-xl p-3 text-center">
          <div className="flex items-center justify-center space-x-1 mb-1">
            <Clock className="w-4 h-4 text-blue-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{formatDuration(data.total_duration_seconds)}</p>
          <p className="text-xs text-gray-500">Talk time</p>
        </div>
      </div>

      {/* Daily bar chart */}
      <div className="mb-5">
        <p className="text-xs text-gray-500 mb-2">Last 7 days</p>
        <div className="flex items-end justify-between space-x-1 h-16">
          {data.daily.map((d, i) => (
            <div key={d.date} className="flex-1 flex flex-col items-center">
              <div
                className="w-full bg-coral/20 rounded-t-md transition-all"
                style={{ height: `${Math.max((d.count / maxDaily) * 48, 2)}px` }}
              >
                {d.count > 0 && (
                  <div
                    className="w-full bg-coral rounded-t-md"
                    style={{ height: '100%' }}
                  />
                )}
              </div>
              <span className="text-[10px] text-gray-400 mt-1">{d.date}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Top contacts */}
      {data.top_contacts.length > 0 && (
        <div>
          <p className="text-xs text-gray-500 mb-2">Most called (30 days)</p>
          <div className="space-y-2">
            {data.top_contacts.slice(0, 3).map(c => (
              <div key={c.id} className="flex items-center space-x-2">
                <Avatar className="w-7 h-7">
                  <AvatarImage src={c.photo} />
                  <AvatarFallback className="bg-teal text-white text-xs">{c.name?.charAt(0)}</AvatarFallback>
                </Avatar>
                <span className="text-sm flex-1 truncate">{c.name}</span>
                <span className="text-xs text-gray-400">{c.count} calls</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CallAnalytics;
